import React, { useCallback, useContext, useMemo, useReducer } from 'react'
import { uid } from 'begonya/functions'

import type {
  FlagcardData,
  FlagcardDispatch,
  FlagcardProps,
  FlagcardState,
} from './types'
import reducer from './reducer'

const initialState: FlagcardState = []

const initialValue: FlagcardProps = {
  dispatchs: {
    add: () => null,
    remove: () => null,
  },
  provided: false,
  state: initialState,
}

export const FlagcardContext = React.createContext<FlagcardProps>(initialValue)

export const FlagcardProvider: React.FunctionComponent = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const add = useCallback(({ id, appearance, ...rest }: FlagcardData) => {
    dispatch({
      type: 'ADD_FLAGCARD',
      payload: {
        id: id || uid(),
        appearance: appearance || 'success',
        ...rest,
      },
    })
  }, [])

  const remove = useCallback((id: string) => {
    dispatch({
      type: 'REMOVE_FLAGCARD',
      payload: id,
    })
  }, [])

  const dispatchs = useMemo(
    () => ({
      add,
      remove,
    }),
    [add, remove]
  )

  return (
    <FlagcardContext.Provider
      value={{
        dispatchs,
        provided: true,
        state,
      }}
    >
      {children}
    </FlagcardContext.Provider>
  )
}

export const useFlagcard = (): [FlagcardDispatch, FlagcardState] => {
  const { state, provided, dispatchs } = useContext(FlagcardContext)

  if (!provided) {
    throw new Error('useFlagcard must be used within a FlagcardProvider.')
  }

  return [dispatchs, state]
}
